import { ReadonlyURLSearchParams } from 'next/navigation';

export const createUrl = (
  pathname: string,
  params: URLSearchParams | ReadonlyURLSearchParams | undefined
) => {
  if (!params) {
    return pathname;
  }
  const paramsString = params.toString();
  const queryString = `${paramsString.length ? '?' : ''}${paramsString}`;

  return `${pathname}${queryString}`;
};

export const ensureStartsWith = (stringToCheck: string, startsWith: string) =>
  stringToCheck.startsWith(startsWith) ? stringToCheck : `${startsWith}${stringToCheck}`;

export const validateEnvironmentVariables = () => {
  const requiredEnvironmentVariables = ['SHOPIFY_STORE_DOMAIN', 'SHOPIFY_STOREFRONT_ACCESS_TOKEN'];
  const missingEnvironmentVariables = [] as string[];

  requiredEnvironmentVariables.forEach((envVar) => {
    if (!process.env[envVar]) {
      missingEnvironmentVariables.push(envVar);
    }
  });

  if (missingEnvironmentVariables.length) {
    throw new Error(
      `The following environment variables are missing. Your site will not work without them. Read more: https://vercel.com/docs/integrations/shopify#configure-environment-variables\n\n${missingEnvironmentVariables.join(
        '\n'
      )}\n`
    );
  }

  if (
    process.env.SHOPIFY_STORE_DOMAIN?.includes('[') ||
    process.env.SHOPIFY_STORE_DOMAIN?.includes(']')
  ) {
    throw new Error(
      'Your `SHOPIFY_STORE_DOMAIN` environment variable includes brackets (ie. `[` and / or `]`). Your site will not work with them there. Please remove them.'
    );
  }
};

export function capitalize(str: string | undefined) {
  if (!str) return str;
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function numberToOrdinal(num: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const value = num % 100;
  return num.toFixed(0) + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]);
}

export function parseFloatOrUndefined(value: string | undefined) {
  return value !== undefined ? parseFloat(value) : undefined;
}

export function parseIntOrUndefined(value: string | undefined) {
  return value !== undefined ? parseInt(value) : undefined;
}

export function parseIntOrNull(value: string | null | undefined) {
  return value !== null && value !== undefined ? parseInt(value) : null;
}

export function parseFloatOrNull(value: string | null | undefined) {
  return value !== null && value !== undefined ? parseFloat(value) : null;
}

// Types

export function assertPresent<T>(value: T): NonNullable<T> {
  if (value === null || value === undefined) {
    throw new Error('Value is null or undefined');
  }
  return value as NonNullable<T>;
}

type NonEmptyArray<T> = [T, ...T[]];

export function assertNonEmpty<T>(value: T[]): NonEmptyArray<T> {
  if (!value.length) {
    throw new Error('Value is empty');
  }
  return value as NonEmptyArray<T>;
}

export function assertKeyOf<T extends object>(mapObject: T, key: string): T[keyof T] {
  if (!(key in mapObject)) {
    throw new Error(`Key ${key} not found in value`);
  }
  return mapObject[key as keyof T];
}

export function safeArray<T>(value: T | T[] | undefined): T[] {
  return value === undefined ? [] : Array.isArray(value) ? value : [value];
}

export function safeFirst<T>(value: T | T[] | undefined): T | undefined {
  return value === undefined ? undefined : Array.isArray(value) ? value[0] : value;
}
export function buildURLSearchParams(searchParams: {
  [key: string]: string | string[] | undefined;
}): URLSearchParams {
  const urlSearchParams = new URLSearchParams();

  Object.entries(searchParams).forEach(([key, value]) => {
    if (value === undefined) {
      // Skip undefined values
      return;
    }

    if (Array.isArray(value)) {
      // Add each array item as a separate parameter for the same key
      value.forEach((val) => urlSearchParams.append(key, val));
    } else {
      // Add single value as a parameter
      urlSearchParams.append(key, value);
    }
  });

  return urlSearchParams;
}
